import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Select,
    MenuItem,
    Avatar,
    Paper,
    Button,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getMediaUrl } from '../../utils/urlHelper';
import { GET_All_EMPLOYEE_DETAILS } from '../../api/index';
import { SELECTED_MENU_PROPS } from '../../components/StyledComponents/StyledComponent';

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Parse a date string (YYYY-MM-DD or DD-MM-YYYY) into {y, m, d}, or null.
const parseDate = (str) => {
    if (!str) return null;
    const parts = String(str).split(/[-/]/);
    if (parts.length !== 3) return null;
    let y, m, d;
    if (parts[0].length === 4) {
        y = +parts[0]; m = +parts[1]; d = +parts[2];
    } else {
        d = +parts[0]; m = +parts[1]; y = +parts[2];
    }
    if (isNaN(y) || isNaN(m) || isNaN(d) || m < 1 || m > 12) return null;
    return { y, m, d };
};

// Single card that toggles between Birthday (date_of_birth) and Work
// Anniversary (date_of_joining) — keeps the dashboard to one compact card.
const CelebrationsWidget = () => {
    const [mode, setMode] = useState('birthday'); // 'birthday' | 'anniversary'
    const [filter, setFilter] = useState('Today');
    const [page, setPage] = useState(0);
    const itemsPerPage = 3;

    const { data: emp, isLoading, isError } = useQuery({
        queryKey: ['employeeListDetailedForCelebrations'],
        queryFn: GET_All_EMPLOYEE_DETAILS,
        staleTime: 1000 * 60 * 5,
    });

    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentDay = today.getDate();
    const currentYear = today.getFullYear();

    const lists = useMemo(() => {
        let todayList = [];
        let upcomingList = [];
        if (!Array.isArray(emp)) return { today: [], upcoming: [] };

        emp.forEach((employee) => {
            const isAnniv = mode === 'anniversary';
            if (isAnniv && employee.resigned) return; // anniversaries: current staff only

            const src = isAnniv
                ? (employee.date_of_joining || employee.doj)
                : (employee.date_of_birth || employee.dob || employee.DOB);
            const parsed = parseDate(src);
            if (!parsed) return;

            const { y: empYear, m: empMonth, d: empDay } = parsed;
            const isToday = (empMonth === currentMonth && empDay === currentDay);

            let years = currentYear - empYear;
            let isUpcoming = false;
            if (!isToday) {
                const dt = new Date(currentYear, empMonth - 1, empDay);
                let diff = Math.ceil((dt.getTime() - today.getTime()) / (1000 * 3600 * 24));
                if (diff < 0) {
                    const next = new Date(currentYear + 1, empMonth - 1, empDay);
                    diff = Math.ceil((next.getTime() - today.getTime()) / (1000 * 3600 * 24));
                    years = (currentYear + 1) - empYear;
                }
                if (diff > 0 && diff <= 182) isUpcoming = true;
            }

            if (isAnniv && years < 1) return; // skip <1 year anniversaries

            const obj = {
                ...employee,
                displayDate: `${monthNames[empMonth - 1]} ${empDay}`,
                years,
                sortVal: empMonth * 100 + empDay,
            };
            if (isToday) todayList.push(obj);
            if (isUpcoming) upcomingList.push(obj);
        });

        upcomingList.sort((a, b) => {
            const cur = currentMonth * 100 + currentDay;
            let va = a.sortVal, vb = b.sortVal;
            if (va < cur) va += 1200;
            if (vb < cur) vb += 1200;
            return va - vb;
        });

        return { today: todayList, upcoming: upcomingList };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emp, mode, currentMonth, currentDay, currentYear]);

    const isAnniv = mode === 'anniversary';
    const displayList = filter === 'Today' ? lists.today : lists.upcoming;
    const totalPages = Math.ceil(displayList.length / itemsPerPage);
    const paginatedList = displayList.slice(page * itemsPerPage, (page + 1) * itemsPerPage);

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 2,
                background: '#fff',
                boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                border: '1px solid #f3f4f6',
                height: 'fit-content',
                width: '100%',
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                {/* Header typography unified with the Market Holidays widget (variant h6 / weight 600). */}
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    {isAnniv ? 'Work Anniversary' : 'Birthday'}
                </Typography>
            </Box>

            {/* Birthday / Anniversary toggle */}
            <ToggleButtonGroup
                value={mode}
                exclusive
                size="small"
                fullWidth
                onChange={(e, v) => { if (v) { setMode(v); setPage(0); } }}
                sx={{
                    mb: 2,
                    '& .MuiToggleButton-root': {
                        textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', py: 0.6, borderRadius: 2,
                        color: '#6b7280', borderColor: '#e5e7eb',
                        '&.Mui-selected': { bgcolor: '#eef2ff', color: '#4f46e5', '&:hover': { bgcolor: '#e0e7ff' } },
                    },
                }}
            >
                <ToggleButton value="birthday">🎂 Birthday</ToggleButton>
                <ToggleButton value="anniversary">🎊 Anniversary</ToggleButton>
            </ToggleButtonGroup>

            <Select
                value={filter}
                onChange={(e) => { setFilter(e.target.value); setPage(0); }}
                size="small"
                fullWidth
                MenuProps={SELECTED_MENU_PROPS}
                sx={{
                    mb: 2,
                    borderRadius: 3,
                    backgroundColor: '#f9fafb',
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    fontWeight: 500,
                    color: '#4b5563',
                }}
            >
                <MenuItem value="Today">Today</MenuItem>
                <MenuItem value="Upcoming">Upcoming</MenuItem>
            </Select>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: '180px' }}>
                {isLoading ? (
                    <Box sx={{ py: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary" fontSize="0.8rem">Loading...</Typography>
                    </Box>
                ) : isError ? (
                    <Box sx={{ py: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="error" fontSize="0.8rem">Error</Typography>
                    </Box>
                ) : paginatedList.length > 0 ? (
                    paginatedList.map((person, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderBottom: idx !== paginatedList.length - 1 ? '1px solid #f3f4f6' : 'none',
                                pb: idx !== paginatedList.length - 1 ? 1.5 : 0,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar
                                    src={getMediaUrl(person.photo)}
                                    sx={{ width: 36, height: 36, bgcolor: '#f4f4f5', color: isAnniv ? '#10b981' : '#6366f1', fontWeight: 600, fontSize: '0.9rem' }}
                                >
                                    {person.name?.charAt(0)}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle2" fontWeight={700} color="#111827" sx={{ fontSize: '0.85rem' }}>
                                        {person.name || 'Employee'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                        {isAnniv
                                            ? `${person.displayDate} · ${person.years} ${person.years === 1 ? 'year' : 'years'}`
                                            : `${person.displayDate} · Happy Birthday`}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ fontSize: '1rem', pb: 1 }}>
                                {isAnniv ? '🎊' : '🎉'}
                            </Box>
                        </Box>
                    ))
                ) : (
                    <Box sx={{ py: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                            No {isAnniv ? 'anniversaries' : 'birthdays'} {filter.toLowerCase()}.
                        </Typography>
                    </Box>
                )}
            </Box>

            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                    <Button
                        disabled={page === 0}
                        onClick={() => setPage(p => p - 1)}
                        size="small"
                        sx={{ minWidth: 30, height: 24, p: 0, textTransform: 'none', fontSize: '0.7rem' }}
                    >
                        Prev
                    </Button>
                    <Typography variant="caption" sx={{ alignSelf: 'center', color: 'text.secondary', fontSize: '0.7rem' }}>
                        {page + 1} / {totalPages}
                    </Typography>
                    <Button
                        disabled={page === totalPages - 1}
                        onClick={() => setPage(p => p + 1)}
                        size="small"
                        sx={{ minWidth: 30, height: 24, p: 0, textTransform: 'none', fontSize: '0.7rem' }}
                    >
                        Next
                    </Button>
                </Box>
            )}
        </Paper>
    );
};

export default CelebrationsWidget;
